// What the SERVER says about the agent's current turn, folded into the one
// shape the Focus wait line reads (`focus/wait-status.ts`).
//
// Three facts, each from a frame the server already sends:
//   - WHEN the busy stretch began: `agent_turn` busy carries `since`, and the
//     attach snapshot repeats the original, so a reload reads the same minutes;
//   - WHAT it is doing: the latest tool call of THIS stretch that the page has
//     a word for (`activityOf`) — a sandbox `tool_use` block on `bridge_stream`
//     (subagents included) or a servant `tool_use_start`. A call it has no word
//     for (`ToolSearch`, `TodoWrite`, …) leaves the line as it was. Only the
//     tool NAME is kept, never its input;
//   - WHY the last stretch ended, when the bridge says: `loom_error
//     idle_timeout` is the bridge killing a `claude` that wrote nothing for five
//     minutes.
//
// Clocks. Every comparison between two frames happens on the SERVER's clock
// (a tool call counts only if its `emittedAt` is at or after the stretch's
// `since`, so a call replayed from an earlier turn never labels this one).
// Every value handed to the page is on the BROWSER's clock, converted without
// comparing the two clocks: clientMs = arrival − (frame.emittedAt − serverMs).

import type { AgentEvent } from "@loom/shared";

export type WaitActivity = "code" | "issue" | "card" | "size" | "design" | "milestones";

/**
 * A tool name → what the user is told Loom is doing. Matched on the part after
 * the LAST `__`, so the sandbox's `mcp__loom__propose_card`, the double-prefixed
 * `mcp__loom__loom__propose_card` and the servant's `loom__propose_card` all
 * land on the same word. Anything else has no word (null), and the fold below
 * keeps the last call that had one.
 */
export const activityOf = (toolName: string): WaitActivity | null => {
  const cut = toolName.lastIndexOf("__");
  const bare = cut === -1 ? toolName : toolName.slice(cut + 2);
  switch (bare) {
    case "Read":
    case "Grep":
    case "Glob":
    case "LS":
    case "Bash":
    case "Task":
    case "Agent":
      // Claude Code 2.1.x names its subagent tool `Agent`, not `Task` (job
      // 6396233: `tool_use Agent` ×4); a subagent reads the code for the turn.
      return "code";
    case "get_issue":
    case "list_issues":
      return "issue";
    case "propose_card":
      return "card";
    case "estimate_story_points":
      return "size";
    case "fetch_figma_design":
      return "design";
    case "list_milestones":
      return "milestones";
    default:
      return null;
  }
};

export interface AgentWork {
  /** When the current busy stretch began, browser clock; null while idle. */
  readonly busySinceMs: number | null;
  /** The latest tool call of this stretch that `activityOf` has a word for
   *  (name only), browser clock. */
  readonly activity: { readonly tool: string; readonly atMs: number } | null;
  /** Why the last stretch stopped, when the bridge said: `silence` = its
   *  5-minute no-output kill. Cleared by the next busy edge. */
  readonly lastStop: "silence" | null;
}

export interface AgentWorkState extends AgentWork {
  /** The stretch's start on the SERVER's clock — what frames are read against. */
  readonly busySinceServerMs: number | null;
  readonly activityServerMs: number | null;
}

export const INITIAL_AGENT_WORK: AgentWorkState = {
  busySinceMs: null,
  busySinceServerMs: null,
  activity: null,
  activityServerMs: null,
  lastStop: null,
};

const parseMs = (iso: unknown): number | null => {
  if (typeof iso !== "string") return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

/** The last tool name in a stream-json `assistant` message, or null. */
const toolUseName = (payload: unknown): string | null => {
  if (!isRecord(payload) || payload.type !== "assistant") return null;
  const message = payload.message;
  if (!isRecord(message) || !Array.isArray(message.content)) return null;
  let name: string | null = null;
  for (const block of message.content) {
    if (isRecord(block) && block.type === "tool_use" && typeof block.name === "string") {
      name = block.name;
    }
  }
  return name;
};

const isSilenceKill = (payload: unknown): boolean =>
  isRecord(payload) && payload.type === "loom_error" && payload.error === "idle_timeout";

/** Does this event move `AgentWork` at all? (So the hook skips the rest.) */
export const touchesAgentWork = (event: { readonly type: string }): boolean =>
  event.type === "agent_turn" ||
  event.type === "bridge_stream" ||
  event.type === "tool_use_start";

export const foldAgentWork = (
  prev: AgentWorkState,
  event: AgentEvent,
  arrivalMs: number,
): AgentWorkState => {
  const emitted = parseMs(event.emittedAt);
  const toClient = (serverMs: number): number =>
    emitted === null ? arrivalMs : arrivalMs - (emitted - serverMs);
  // A frame belongs to THIS stretch iff it was emitted at or after its start.
  const inStretch = (): boolean =>
    prev.busySinceServerMs !== null && emitted !== null && emitted >= prev.busySinceServerMs;

  if (event.type === "agent_turn") {
    if (event.state === "idle") {
      if (prev.busySinceMs === null && prev.activity === null) return prev;
      return {
        ...prev,
        busySinceMs: null,
        busySinceServerMs: null,
        activity: null,
        activityServerMs: null,
      };
    }
    const since = parseMs((event as { since?: unknown }).since);
    const edge = prev.busySinceMs === null;
    // No `since` (an older server): keep a stretch already running, else the
    // stretch starts at this frame.
    const serverSince = since ?? (edge ? emitted : prev.busySinceServerMs) ?? emitted;
    // The conversion is exact only for a frame the server wrote the moment it
    // was sent. A frame delivered LATE — the same stretch's busy edge re-sent
    // after the attach snapshot — reads as a stretch that began on arrival:
    // live on #290 a reload put "· 1 min" back to nothing, 124 s into the turn.
    // Delay only ever pushes the conversion later, so for the SAME stretch the
    // earliest reading is the truest one, and it is kept.
    const sameStretch =
      since !== null && !edge && prev.busySinceServerMs === since && prev.busySinceMs !== null;
    const clientSince =
      since !== null
        ? sameStretch
          ? Math.min(prev.busySinceMs as number, toClient(since))
          : toClient(since)
        : edge || prev.busySinceMs === null
          ? arrivalMs
          : prev.busySinceMs;
    const keepActivity =
      prev.activity !== null &&
      prev.activityServerMs !== null &&
      serverSince !== null &&
      prev.activityServerMs >= serverSince;
    return {
      busySinceMs: clientSince,
      busySinceServerMs: serverSince,
      activity: keepActivity ? prev.activity : null,
      activityServerMs: keepActivity ? prev.activityServerMs : null,
      lastStop: edge ? null : prev.lastStop,
    };
  }

  if (event.type === "tool_use_start") {
    if (!inStretch() || emitted === null || activityOf(event.toolName) === null) return prev;
    return {
      ...prev,
      activity: { tool: event.toolName, atMs: toClient(emitted) },
      activityServerMs: emitted,
    };
  }

  if (event.type === "bridge_stream") {
    const payload = (event as { payload?: unknown }).payload;
    if (payload === undefined) return prev;
    if (isSilenceKill(payload)) {
      if (!inStretch()) return prev;
      return { ...prev, lastStop: "silence" };
    }
    const tool = toolUseName(payload);
    if (tool === null || activityOf(tool) === null || !inStretch() || emitted === null) {
      return prev;
    }
    return {
      ...prev,
      activity: { tool, atMs: toClient(emitted) },
      activityServerMs: emitted,
    };
  }

  return prev;
};

/**
 * A phase's start on the browser's clock: the server's `since` across the
 * skew-free rule, else the start already held for the SAME phase, else now.
 */
export const phaseSinceClientMs = (
  event: { readonly emittedAt: string; readonly since?: string; readonly phase: string },
  prev: { readonly phase: string; readonly sinceMs?: number },
  arrivalMs: number,
): number => {
  const since = parseMs(event.since);
  const emitted = parseMs(event.emittedAt);
  if (since !== null && emitted !== null) return arrivalMs - (emitted - since);
  if (prev.phase === event.phase && prev.sinceMs !== undefined) return prev.sinceMs;
  return arrivalMs;
};
